// Las consultas de WhatsApp (y de ANDI) sobre `whatsappThreads`, escritas dos
// veces: la de Firestore (en cada pantalla, se usa si el VPS no responde) y
// aquí su equivalente sobre el espejo del VPS (src/shared/vpsMirror.ts). Tienen
// que devolver lo mismo y en el mismo orden, como en ../Andi/lecturasCrm.ts:
// - `==` solo casa con el mismo valor y tipo: un chat sin `area` NO entra en
//   `where("area", "==", "comercial")`, aunque el contador lo cuente como
//   comercial;
// - `orderBy("lastMessageAt", "desc")` deja fuera los chats sin fecha y
//   desempata por id, también descendente; sin `orderBy`, por id ascendente.
import { Timestamp } from "firebase/firestore";
import type { Fila } from "../../shared/vpsMirror";

export const LIMITE_LISTA = 150;
export const LIMITE_BUSQUEDA = 30;
export const LIMITE_ANTERIORES = 100;

const esFecha = (v: any): v is Timestamp => v instanceof Timestamp;
const porIdAsc = (a: Fila, b: Fila) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
const porUltimoDesc = (a: Fila, b: Fila) =>
  b.lastMessageAt.toMillis() - a.lastMessageAt.toMillis() || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);

const deArea = (filas: Fila[], area: string) => filas.filter((f) => f.area === area && esFecha(f.lastMessageAt));

// where("area", "==", area), orderBy("lastMessageAt", "desc"), limit(150)
export function recientesDeArea(area: string, limite = LIMITE_LISTA) {
  return (filas: Fila[]) => deArea(filas, area).sort(porUltimoDesc).slice(0, limite);
}

// ...lo mismo con startAfter(lastMessageAt del último), limit(100). Un cursor
// con solo el valor de la fecha salta TODOS los chats con esa misma fecha.
export function anterioresDeArea(area: string, antesDeMs: number, limite = LIMITE_ANTERIORES) {
  return (filas: Fila[]) => deArea(filas, area)
    .filter((f) => f.lastMessageAt.toMillis() < antesDeMs)
    .sort(porUltimoDesc)
    .slice(0, limite);
}

// where("area", "==", area), where("searchPrefixes", "array-contains", token),
// orderBy("lastMessageAt", "desc"), limit(30)
export function buscarEnArea(area: string, token: string, limite = LIMITE_BUSQUEDA) {
  return (filas: Fila[]) => deArea(filas, area)
    .filter((f) => Array.isArray(f.searchPrefixes) && f.searchPrefixes.includes(token))
    .sort(porUltimoDesc)
    .slice(0, limite);
}

// where("hasUnread", "==", true)
export const noLeidos = (filas: Fila[]) => filas.filter((f) => f.hasUnread === true).sort(porIdAsc);

// doc("whatsappThreads", id): una fila o ninguna.
export const chatPorId = (id: string) => (filas: Fila[]) => filas.filter((f) => f.id === id);

// Firestore ordena primero por tipo (null < booleano < número < fecha < texto
// < lo demás) y luego por valor.
const rangoTipo = (v: any) =>
  v === null ? 0 : typeof v === "boolean" ? 1 : typeof v === "number" ? 2 :
    v instanceof Timestamp ? 3 : typeof v === "string" ? 4 : 5;
function compararValor(a: any, b: any) {
  const ra = rangoTipo(a);
  const rb = rangoTipo(b);
  if (ra !== rb) return ra - rb;
  if (ra === 1 || ra === 2) return Number(a) - Number(b);
  if (ra === 3) return a.toMillis() - b.toMillis();
  if (ra === 4) return a < b ? -1 : a > b ? 1 : 0;
  return 0;
}

// Mensajes de un chat como los daban las páginas de Firestore:
// orderBy("at", "desc") dado la vuelta, así que el más antiguo primero y los
// empates por id ascendente. Sin campo `at`, Firestore no los devuelve.
export const mensajesEnOrden = (filas: Fila[]) => filas
  .filter((f) => f.at !== undefined)
  .sort((a, b) => compararValor(a.at, b.at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
